// Service for Grade 11-12 (JEE/NEET) concepts, flashcards, tests, and PYQs

import { supabase, supabaseUrl, supabaseAnonKey } from "./supabaseClient";
import type { Flashcard, Test, TestQuestion, ConceptPlus1, PYQReference } from "../types";
import { FEATURES } from "../config/features";
import { getRegion } from "../config/regionConfig";

// ============================================
// Additional Types
// ============================================

export interface Badge {
  badge_id: number;
  id: number;
  badge_name: string;
  name: string;
  badge_description: string;
  description: string;
  badge_type: string;
  icon_url: string | null;
  criteria: string | null;
  earned_at: string | null;
  is_earned: boolean;
}

export interface FlashcardPlus1 {
  id: number;
  concept_id: number;
  question: string;
  answer: string;
  concept_name?: string;
}

export interface TestQuestionPlus1 {
  id: number;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option_index: number;
  concept_tag: string | null;
  difficulty_score: number | null;
}

export interface ConceptWithPYQ {
  concept: {
    id: number;
    name: string;
    subject_id: number | null;
    explanation_standard: string | null;
  };
  pyq_history: PYQReference[];
  pyq_count: number;
}

// ============================================
// Concept Functions
// ============================================

/**
 * Fetch all concepts from concepts_plus1 table
 */
export async function fetchConceptsPlus1(
  subjectId?: number,
  grade?: number
): Promise<ConceptPlus1[]> {
  try {
    const { data, error } = await supabase.rpc('get_concepts_plus1', {
      p_subject_id: subjectId || null,
      p_grade: grade || null
    });

    if (error) {
      console.error('Error fetching concepts_plus1:', error);
      return [];
    }

    return (data || []) as ConceptPlus1[];
  } catch (error) {
    console.error('Failed to fetch concepts_plus1:', error);
    return [];
  }
}

/**
 * Phase 8 (Prepare for a Test port): fetch full concept detail for a specific
 * set of ids -- used to hydrate a queue once the student confirms which
 * concepts to include, mirroring rewise-ui's fetchConceptsByIds. A direct
 * table select (not the get_concepts_plus1 RPC) since it selects its own
 * column list, sidestepping any uncertainty about that RPC's completeness.
 *
 * Note: subject_name is NOT a real column on concepts_plus1 (confirmed live:
 * a direct select including it 42703s -- "column does not exist"; the RPC
 * must compute/join it server-side). Not selected here since callers already
 * carry subject context from the picking flow (mastery state rows), not from
 * these hydrated concept objects.
 */
export async function fetchConceptsPlus1ByIds(ids: number[]): Promise<ConceptPlus1[]> {
  if (ids.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('concepts_plus1')
      .select(`
        id, name, subject_id, concept_type, grade_range,
        explanation_basic, explanation_standard, explanation_advanced,
        formula_summary, common_mistakes, tips_and_tricks, real_life_example
      `)
      .in('id', ids);

    if (error) {
      console.error('Error fetching concepts_plus1 by ids:', error);
      return [];
    }
    return (data || []) as ConceptPlus1[];
  } catch (error) {
    console.error('Failed to fetch concepts_plus1 by ids:', error);
    return [];
  }
}

/**
 * Fetch all subjects for Plus1 (grades 11-12)
 */
export async function fetchSubjectsPlus1(): Promise<Record<number, string>> {
  try {
    const { data, error } = await supabase
      .from('subjects_plus1')
      .select('id, name')
      .order('name');

    if (error) {
      console.error('Error fetching subjects_plus1:', error);
      return {};
    }

    const subjectsMap: Record<number, string> = {};
    (data || []).forEach((subject: { id: number; name: string }) => {
      subjectsMap[subject.id] = subject.name;
    });

    return subjectsMap;
  } catch (error) {
    console.error('Failed to fetch subjects_plus1:', error);
    return {};
  }
}

/**
 * Fetch a single concept with full details including flashcards
 */
export async function fetchConceptPlus1Detail(
  conceptId: number
): Promise<ConceptPlus1 | null> {
  try {
    const { data, error } = await supabase.rpc('get_concept_plus1_detail', {
      p_concept_id: conceptId
    });

    if (error) {
      console.error('Error fetching concept_plus1 detail:', error);
      return null;
    }

    if (!data) return null;

    // Transform flashcards to match Flashcard interface
    const transformed: ConceptPlus1 = {
      ...data,
      flashcards: (data.flashcards || []).map((f: { question: string; answer: string }) => ({
        front: f.question,
        back: f.answer
      }))
    };

    return transformed;
  } catch (error) {
    console.error('Failed to fetch concept_plus1 detail:', error);
    return null;
  }
}

/**
 * Fetch concept with difficulty-specific explanation
 */
export async function fetchConceptPlus1WithDifficulty(
  conceptId: number,
  difficulty: 'basic' | 'standard' | 'advanced' = 'standard'
): Promise<ConceptPlus1 | null> {
  const concept = await fetchConceptPlus1Detail(conceptId);

  if (!concept) return null;

  const explanationKey = `explanation_${difficulty}` as keyof ConceptPlus1;
  const selectedExplanation = concept[explanationKey] as string | null;

  return {
    ...concept,
    explanation_standard: selectedExplanation || concept.explanation_standard
  };
}

// ============================================
// Flashcard Functions
// ============================================

/**
 * Fetch flashcards for a specific concept
 */
export async function fetchFlashcardsPlus1(
  conceptId: number
): Promise<FlashcardPlus1[]> {
  try {
    const { data, error } = await supabase.rpc('get_flashcards_plus1', {
      p_concept_id: conceptId
    });

    if (error) {
      console.error('Error fetching flashcards_plus1:', error);
      return [];
    }

    return (data || []) as FlashcardPlus1[];
  } catch (error) {
    console.error('Failed to fetch flashcards_plus1:', error);
    return [];
  }
}

/**
 * Fetch random flashcards for a subject (revision mode)
 */
export async function fetchFlashcardsPlus1BySubject(
  subjectId: number,
  grade?: number,
  limit: number = 50
): Promise<FlashcardPlus1[]> {
  try {
    const { data, error } = await supabase.rpc('get_flashcards_plus1_by_subject', {
      p_subject_id: subjectId,
      p_grade: grade || null,
      p_limit: limit
    });

    if (error) {
      console.error('Error fetching flashcards_plus1 by subject:', error);
      return [];
    }

    return (data || []) as FlashcardPlus1[];
  } catch (error) {
    console.error('Failed to fetch flashcards_plus1 by subject:', error);
    return [];
  }
}

/**
 * Transform FlashcardPlus1 to Flashcard interface
 */
export function transformFlashcardsPlus1(flashcards: FlashcardPlus1[]): Flashcard[] {
  return flashcards.map(f => ({
    front: f.question,
    back: f.answer
  }));
}

// ============================================
// Test Question Functions
// ============================================

/**
 * Fetch test questions for a test by test ID
 */
export async function fetchTestQuestionsPlus1ByTestId(
  testId: number
): Promise<TestQuestionPlus1[]> {
  try {
    const { data, error } = await supabase.rpc('get_test_questions_plus1', {
      p_test_id: testId
    });

    if (error) {
      console.error('Error fetching test_questions_plus1:', error);
      return [];
    }

    return (data || []) as TestQuestionPlus1[];
  } catch (error) {
    console.error('Failed to fetch test_questions_plus1:', error);
    return [];
  }
}

/**
 * Fetch test questions for a concept (for inline tests on concept page)
 */
export async function fetchTestQuestionsPlus1(
  conceptId: number,
  difficulty?: string,
  limit: number = 10
): Promise<TestQuestionPlus1[]> {
  try {
    const { data, error } = await supabase.rpc('get_test_questions_by_concept_plus1', {
      p_concept_id: conceptId,
      p_difficulty: difficulty || null,
      p_limit: limit
    });

    if (error) {
      console.error('Error fetching test_questions_plus1 by concept:', error);
      return [];
    }

    return (data || []) as TestQuestionPlus1[];
  } catch (error) {
    console.error('Failed to fetch test_questions_plus1 by concept:', error);
    return [];
  }
}

/**
 * Fetch test with questions combined
 */
export async function fetchTestPlus1WithQuestions(
  testId: number
): Promise<Test | null> {
  try {
    const { data, error } = await supabase.rpc('get_test_plus1_with_questions', {
      p_test_id: testId
    });

    if (error) {
      console.error('Error fetching test_plus1 with questions:', error);
      return null;
    }

    if (!data) return null;

    const transformed: Test = {
      id: String(data.id),
      difficulty: data.difficulty || 'standard',
      description: data.description,
      questions: transformTestQuestionsPlus1(data.questions || [])
    };

    return transformed;
  } catch (error) {
    console.error('Failed to fetch test_plus1 with questions:', error);
    return null;
  }
}

/**
 * Transform TestQuestionPlus1 to TestQuestion interface
 */
export function transformTestQuestionsPlus1(questions: TestQuestionPlus1[]): TestQuestion[] {
  return questions.map(q => ({
    id: String(q.id),
    question: q.question,
    options: [q.option_a, q.option_b, q.option_c, q.option_d],
    correctOptionIndex: q.correct_option_index,
    conceptTag: q.concept_tag || undefined,
    difficultyScore: q.difficulty_score || undefined
  }));
}

// ============================================
// PYQ Reference Functions
// ============================================

/**
 * Fetch PYQs linked to a concept
 */
export async function fetchPYQByConcept(
  conceptId: number
): Promise<PYQReference[]> {
  try {
    const { data, error } = await supabase.rpc('get_pyq_by_concept', {
      p_concept_id: conceptId
    });

    if (error) {
      console.error('Error fetching PYQ by concept:', error);
      return [];
    }

    return (data || []) as PYQReference[];
  } catch (error) {
    console.error('Failed to fetch PYQ by concept:', error);
    return [];
  }
}

/**
 * Fetch PYQs by exam and optional year
 */
export async function fetchPYQByExam(
  exam: string,
  year?: number
): Promise<PYQReference[]> {
  try {
    const { data, error } = await supabase.rpc('get_pyq_by_exam', {
      p_exam: exam,
      p_year: year || null
    });

    if (error) {
      console.error('Error fetching PYQ by exam:', error);
      return [];
    }

    return (data || []) as PYQReference[];
  } catch (error) {
    console.error('Failed to fetch PYQ by exam:', error);
    return [];
  }
}

/**
 * Fetch concept with its PYQ history
 */
export async function fetchConceptPlus1WithPYQ(
  conceptId: number
): Promise<ConceptWithPYQ | null> {
  try {
    const { data, error } = await supabase.rpc('get_concept_plus1_with_pyq', {
      p_concept_id: conceptId
    });

    if (error) {
      console.error('Error fetching concept_plus1 with PYQ:', error);
      return null;
    }

    return data as ConceptWithPYQ;
  } catch (error) {
    console.error('Failed to fetch concept_plus1 with PYQ:', error);
    return null;
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Get PYQ badge text for a concept
 */
export function getPYQBadgeText(pyqHistory: PYQReference[]): string | null {
  if (!pyqHistory || pyqHistory.length === 0) return null;

  const byExam = pyqHistory.reduce((acc, pyq) => {
    if (!acc[pyq.exam]) acc[pyq.exam] = [];
    if (!acc[pyq.exam].includes(pyq.year)) {
      acc[pyq.exam].push(pyq.year);
    }
    return acc;
  }, {} as Record<string, number[]>);

  const parts = Object.entries(byExam).map(([exam, years]) => {
    const sortedYears = years.sort((a, b) => b - a).slice(0, 3);
    return `${exam} ${sortedYears.join(', ')}`;
  });

  return parts.join(' | ');
}

/**
 * Check if a concept is high-yield based on PYQ frequency
 */
export function isHighYieldConcept(pyqCount: number): boolean {
  return pyqCount >= 3;
}

export async function getStudentBadges(studentId: string): Promise<Badge[]> {
  try {
    const { data, error } = await supabase.rpc('get_student_badges', {
      p_student_id: studentId
    });
    if (error) {
      console.error('Error fetching student badges:', error);
      return [];
    }
    return (data || []) as Badge[];
  } catch (error) {
    console.error('Failed to fetch student badges:', error);
    return [];
  }
}

export interface BadgeAwardResult {
  success: boolean;
  new_badges?: Badge[];
}

export async function awardBadgesOnConcept(studentId: string, conceptId: number): Promise<BadgeAwardResult> {
  try {
    const { data, error } = await supabase.rpc('award_student_badges_on_concept', {
      p_student_id: studentId,
      p_concept_id: conceptId,
    });
    if (error) return { success: false };
    return data as BadgeAwardResult;
  } catch {
    return { success: false };
  }
}

export async function awardBadgesOnTest(studentId: string, score: number): Promise<BadgeAwardResult> {
  try {
    const { data, error } = await supabase.rpc('award_student_badges_on_test', {
      p_student_id: studentId,
      p_score: score,
    });
    if (error) return { success: false };
    return data as BadgeAwardResult;
  } catch {
    return { success: false };
  }
}

interface PerformanceBadgeCandidate {
  badge: Badge;
  priority: number;
  category: 'score' | 'growth';
}

async function getPerformanceBadges(): Promise<Badge[]> {
  try {
    const { data, error } = await supabase.from('badges').select('*').eq('badge_type', 'performance');
    if (error) return [];
    return (data || []).map(b => ({
      badge_id: b.id, id: b.id,
      badge_name: b.name, name: b.name,
      badge_description: b.description, description: b.description,
      badge_type: 'performance' as const,
      icon_url: b.icon_url, criteria: b.criteria,
      earned_at: null, is_earned: false,
    }));
  } catch {
    return [];
  }
}

async function getSubjectTestHistory(studentId: string, subject: string): Promise<number[]> {
  try {
    const { data, error } = await supabase
      .from('user_test_results')
      .select('score, total, tests!inner(concepts!inner(subject))')
      .eq('user_id', studentId)
      .order('submitted_at', { ascending: true });
    if (error) return [];
    return (data || [])
      .filter((r: any) => r.tests?.concepts?.subject === subject)
      .map((r: any) => Math.round((r.score / r.total) * 100));
  } catch {
    return [];
  }
}

async function hasEarnedBadge(studentId: string, badgeId: number): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('student_badges')
      .select('id')
      .eq('student_id', studentId)
      .eq('badge_id', badgeId);
    if (error) return false;
    return (data?.length || 0) > 0;
  } catch {
    return false;
  }
}

async function awardBadge(studentId: string, badgeId: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('student_badges')
      .insert({ student_id: studentId, badge_id: badgeId, earned_at: new Date().toISOString(), progress: 100 });
    return !error;
  } catch {
    return false;
  }
}

async function getSubjectFromConcept(conceptId: number): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('concepts_plus1')
      .select('subject_id')
      .eq('id', conceptId)
      .maybeSingle();
    if (error || !data) return 'unknown';
    return String(data.subject_id ?? 'unknown');
  } catch {
    return 'unknown';
  }
}

async function evaluatePerformanceBadges(
  studentId: string,
  score: number,
  total: number,
  subject: string,
): Promise<PerformanceBadgeCandidate[]> {
  const percentage = Math.round((score / total) * 100);
  const candidates: PerformanceBadgeCandidate[] = [];
  if (subject === 'unknown') return candidates;

  const allBadges = await getPerformanceBadges();
  const history = await getSubjectTestHistory(studentId, subject);
  const previousScores = history.slice(0, -1);

  for (const badge of allBadges) {
    try {
      const criteria = JSON.parse(badge.criteria || '{}');
      if (criteria.subject && criteria.subject.toLowerCase() !== subject.toLowerCase()) continue;
      if (criteria.one_time && await hasEarnedBadge(studentId, badge.badge_id!)) continue;

      if (criteria.min_score && !criteria.first_time && !criteria.improved_over_previous) {
        if (percentage >= criteria.min_score) {
          candidates.push({ badge, priority: criteria.min_score, category: 'score' });
        }
      }
      if (criteria.first_time && criteria.min_score === 80 && criteria.scope === 'subject') {
        if (percentage >= 80 && !await hasEarnedBadge(studentId, badge.badge_id!)) {
          candidates.push({ badge, priority: 200, category: 'growth' });
        }
      }
      if (criteria.improved_over_previous && criteria.scope === 'subject') {
        if (previousScores.length > 0) {
          const prevBest = Math.max(...previousScores);
          if (percentage > prevBest) candidates.push({ badge, priority: 190, category: 'growth' });
        }
      }
      if (criteria.score_delta && criteria.delta_type === 'absolute_points') {
        if (previousScores.length > 0) {
          const lastScore = previousScores[previousScores.length - 1];
          if (percentage - lastScore >= criteria.score_delta) {
            candidates.push({ badge, priority: 180, category: 'growth' });
          }
        }
      }
      if (criteria.previous_score_below && criteria.current_score_above) {
        if (previousScores.length > 0) {
          const lastScore = previousScores[previousScores.length - 1];
          if (lastScore < criteria.previous_score_below && percentage >= criteria.current_score_above) {
            candidates.push({ badge, priority: 170, category: 'growth' });
          }
        }
      }
    } catch {
      // skip malformed badge criteria
    }
  }
  return candidates;
}

function selectBadgesToDisplay(candidates: PerformanceBadgeCandidate[]): Badge[] {
  const scoreBadges = candidates.filter(c => c.category === 'score').sort((a, b) => b.priority - a.priority);
  const growthBadges = candidates.filter(c => c.category === 'growth').sort((a, b) => b.priority - a.priority);
  const toDisplay: Badge[] = [];
  if (scoreBadges.length > 0) toDisplay.push(scoreBadges[0].badge);
  if (growthBadges.length > 0) toDisplay.push(growthBadges[0].badge);
  return toDisplay;
}

export async function awardPerformanceBadges(
  studentId: string,
  score: number,
  total: number,
  conceptId?: number,
): Promise<Badge[]> {
  const subject = conceptId ? await getSubjectFromConcept(conceptId) : 'general';
  const candidates = await evaluatePerformanceBadges(studentId, score, total, subject);
  const toDisplay = selectBadgesToDisplay(candidates);
  for (const candidate of candidates) {
    await awardBadge(studentId, candidate.badge.badge_id!);
  }
  return toDisplay;
}

// ============================================
// Authentication & MPIN Functions
// ============================================

export interface StudentLogin {
  success: boolean;
  student_id?: string;
  student_code?: string;
  school_id?: number;
  grade_level?: number;
  section_id?: number;
  section_name?: string;
  expires_at?: string;
  expiry_date?: string;
  plan_type?: 'core' | 'premium';
  board?: string;
  state_board?: string; // Specific state (e.g. Maharashtra) for State Board exam differentiation
  error?: string;
}

export interface VerifyStudentResponse {
  success: boolean;
  student_id?: string;
  student_code?: string;
  first_name?: string;
  has_mpin?: boolean;
  requires_setup?: boolean;
  error?: string;
}

export interface MpinResponse {
  success: boolean;
  mpin?: string;
  message?: string;
  error?: string;
}

export interface SignupSourceResponse {
  success: boolean;
  signup_source?: 'school_license' | 'b2c_direct';
  error?: string;
}

/**
 * Login a student using their student code and MPIN
 */
export async function loginStudent(studentCode: string, mpin: string): Promise<StudentLogin> {
  try {
    const { data, error } = await supabase.rpc('login_student', {
      p_student_code: studentCode,
      p_mpin: mpin
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as StudentLogin;
  } catch {
    return { success: false, error: 'Failed to login' };
  }
}

/**
 * Verify student code and check MPIN status
 */
export async function verifyStudentCode(studentCode: string): Promise<VerifyStudentResponse> {
  try {
    const { data, error } = await supabase.rpc('verify_student_code', {
      p_student_code: studentCode
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as VerifyStudentResponse;
  } catch {
    return { success: false, error: 'Failed to verify student code' };
  }
}

/**
 * Set MPIN for first-time setup (student has no MPIN yet)
 */
export async function setStudentMpin(
  studentCode: string,
  parentCode: string,
  newMpin: string
): Promise<MpinResponse> {
  try {
    const { data, error } = await supabase.rpc('set_student_mpin', {
      p_student_code: studentCode,
      p_parent_code: parentCode,
      p_new_mpin: newMpin
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as MpinResponse;
  } catch {
    return { success: false, error: 'Failed to set MPIN' };
  }
}

/**
 * Reset MPIN for a student who forgot their MPIN
 * User chooses their own new MPIN
 */
export async function resetStudentMpin(
  studentCode: string,
  parentCode: string,
  newMpin?: string
): Promise<MpinResponse> {
  try {
    const { data, error } = await supabase.rpc('reset_student_mpin', {
      p_student_code: studentCode,
      p_parent_code: parentCode,
      p_new_mpin: newMpin || null
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as MpinResponse;
  } catch {
    return { success: false, error: 'Failed to reset MPIN' };
  }
}

/**
 * Look up a student's signup_source (school_license vs b2c_direct) before
 * showing the "Forgot MPIN" flow, so B2C students (no Parent Linking Code)
 * get a different screen than B2B/school-linked students.
 */
export async function getStudentSignupSource(studentCode: string): Promise<SignupSourceResponse> {
  try {
    const { data, error } = await supabase.rpc('get_student_signup_source', {
      p_student_code: studentCode
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data as SignupSourceResponse;
  } catch {
    return { success: false, error: 'Failed to look up account type' };
  }
}

export interface SessionCheck {
  active: boolean;
  expires_at?: string;
  grade_level?: number;
  error?: string;
}

/**
 * Check if a student session is active - validates against backend
 */
export async function checkSession(studentId: string): Promise<SessionCheck> {
  try {
    const { data, error } = await supabase.rpc('check_student_session', {
      p_student_id: studentId
    });

    if (error) {
      return { active: false, error: error.message };
    }

    return data as SessionCheck;
  } catch {
    return { active: false, error: 'Failed to check session' };
  }
}

// ============================================
// Chinni AI Template Functions
// ============================================

export interface ChinniTemplate {
  id: number;
  template_key: string;
  template_text: string;
  description: string;
  placeholder_count: number;
}

export interface ConversationTurn {
  question: string;
  answer: string;
}

export interface AskChinniTemplateRequest {
  student_id: string;
  template_id: number;
  topic: string;
  topic2?: string;
  grade_level: number;
  conversationHistory?: ConversationTurn[];
}

export interface AskChinniTemplateResponse {
  answer: string;
  from_cache: boolean;
  doubts_remaining: number | null; // null = unlimited (B2C family-plan students)
  error?: string;
}

/**
 * Get student's doubt quota information
 */
export async function getStudentDoubtQuota(
  studentId: string
): Promise<{ doubts_used: number; doubts_remaining: number | null; unlimited: boolean } | null> {
  if (!FEATURES.CHATBOT) return null;

  try {
    const { data, error } = await supabase.rpc('check_student_session', {
      p_student_id: studentId
    });

    if (error) {
      console.error('Error fetching doubt quota:', error);
      return null;
    }

    if (data && data.active) {
      const unlimited = data.unlimited === true;
      return {
        doubts_used: data.doubts_used || 0,
        doubts_remaining: unlimited ? null : (data.doubts_remaining ?? 500),
        unlimited
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch doubt quota:', error);
    return null;
  }
}

/**
 * Get available Chinni templates
 * Uses direct table query
 */
export async function getChinniTemplates(): Promise<ChinniTemplate[]> {
  if (!FEATURES.CHATBOT) return [];

  try {
    const { data, error } = await supabase
      .from('chinni_templates')
      .select('id, template_key, template_text, description, placeholder_count')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching templates:', error);
      return [];
    }

    return (data || []) as ChinniTemplate[];
  } catch (error) {
    console.error('Failed to fetch templates:', error);
    return [];
  }
}

/**
 * Ask Chinni using template (with caching + logging for teacher/parent visibility)
 */
export async function askChinniTemplate(
  request: AskChinniTemplateRequest
): Promise<AskChinniTemplateResponse> {
  if (!FEATURES.CHATBOT) {
    return {
      answer: '',
      from_cache: false,
      doubts_remaining: 0,
      error: 'Chatbot feature is disabled',
    };
  }

  try {
    // 1. Validate and log usage (edge function handles its own cache via SHA-256)
    const { data: usageResult } = await supabase.rpc('ask_chinni_template', {
      p_student_id: request.student_id,
      p_template_id: request.template_id,
      p_topic: request.topic,
      p_topic2: request.topic2 || null,
      p_from_cache: false,
    });

    if (!usageResult?.success) {
      return {
        answer: '',
        from_cache: false,
        doubts_remaining: usageResult?.doubts_remaining || 0,
        error: usageResult?.error || 'Failed to process request',
      };
    }

    // 3. Call Edge Function for AI response (pass conversation history for multi-turn context)
    const response = await fetch(`${supabaseUrl}/functions/v1/ask-chinni-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        template_id: request.template_id,
        template_key: usageResult.template_key,
        question: usageResult.question,
        topic: request.topic,
        topic2: request.topic2,
        grade_level: request.grade_level,
        region: getRegion(),
        conversation_history: request.conversationHistory ?? [],
        student_id: request.student_id,
      }),
    });

    if (!response.ok) {
      console.error('Edge Function error:', response.status, response.statusText);
      let errorMessage = 'AI service is temporarily unavailable. Please try again later.';
      try {
        const errorData = await response.json();
        console.error('Edge Function error details:', errorData);
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // Response might not be JSON
        console.error('Could not parse error response');
      }
      return {
        answer: '',
        from_cache: false,
        doubts_remaining: usageResult.doubts_remaining,
        error: errorMessage,
      };
    }

    const aiResult = await response.json();



    // 5. Log query for teacher/parent visibility
    const { error: logError } = await supabase.rpc('log_chinni_query', {
      p_student_id: request.student_id,
      p_template_id: request.template_id,
      p_topic: request.topic,
      p_topic2: request.topic2 || null,
      p_question_text: usageResult.question,
      p_response_text: aiResult.answer,
      p_from_cache: false,
      p_grade_level: request.grade_level,
    });
    if (logError) console.error('[Chinni] log_chinni_query failed:', logError);

    return {
      answer: aiResult.answer,
      from_cache: false,
      doubts_remaining: usageResult.doubts_remaining,
    };
  } catch (error) {
    console.error('Template query failed:', error);
    return {
      answer: '',
      from_cache: false,
      doubts_remaining: 0,
      error: 'Failed to get answer',
    };
  }
}

// ─────────────────────────────────────────────────────────────
// Explore Panel — types and service functions
// ─────────────────────────────────────────────────────────────

export interface ArtifactBlob {
  predict: { question: string; options: string[]; correct_index: number };
  artifact_html: string;
  reflect: { question: string; options: string[]; correct_index: number; explanation: string };
}

export interface ConceptMapData {
  generated: boolean;
  needs_first: { id: number; name: string; subject_id?: number }[];
  leads_to:    { id: number; name: string; subject_id?: number }[];
  related:     { id: number; name: string; subject_id?: number }[];
}

export function getGradeBand(gradeLevel: number): '6-8' | '9-10' | '11-12' {
  if (gradeLevel <= 8) return '6-8';
  if (gradeLevel <= 10) return '9-10';
  return '11-12';
}

async function _callTemplateWithCacheKey(params: {
  studentId: string;
  conceptName: string;
  gradeLevel: number;
  templateId: number;
  templateKey: string;
  cacheKey: string;
  topic2?: string;
}): Promise<AskChinniTemplateResponse> {
  const { data: usageResult } = await supabase.rpc('ask_chinni_template', {
    p_student_id:  params.studentId,
    p_template_id: params.templateId,
    p_topic:       params.conceptName,
    p_topic2:      params.topic2 ?? null,
    p_from_cache:  false,
  });
  if (!usageResult?.success) {
    return { answer: '', from_cache: false, doubts_remaining: usageResult?.doubts_remaining ?? 0, error: usageResult?.error ?? 'Failed' };
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/ask-chinni-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify({
      template_id:  params.templateId,
      template_key: params.templateKey,
      question:     usageResult.question,
      topic:        params.conceptName,
      topic2:       params.topic2,
      grade_level:  params.gradeLevel,
      region:       getRegion(),
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    return { answer: '', from_cache: false, doubts_remaining: usageResult.doubts_remaining, error: err.error ?? 'Edge function error' };
  }
  const aiResult = await response.json();

  return { answer: aiResult.answer, from_cache: false, doubts_remaining: usageResult.doubts_remaining };
}

export async function askStoryMode(params: {
  studentId: string; conceptName: string; theme: string; gradeLevel: number;
}): Promise<AskChinniTemplateResponse> {
  return _callTemplateWithCacheKey({
    studentId: params.studentId, conceptName: params.conceptName, gradeLevel: params.gradeLevel,
    templateId: 13, templateKey: 'story_mode',
    cacheKey: `${params.conceptName}::${params.theme}`, topic2: params.theme,
  });
}

export async function makeDifficultyExplanation(params: {
  studentId: string; conceptName: string; gradeLevel: number; level: 'basic' | 'advanced';
}): Promise<AskChinniTemplateResponse> {
  return _callTemplateWithCacheKey({
    studentId: params.studentId, conceptName: params.conceptName, gradeLevel: params.gradeLevel,
    templateId: params.level === 'basic' ? 14 : 15,
    templateKey: params.level === 'basic' ? 'difficulty_basic' : 'difficulty_advanced',
    cacheKey: `${params.conceptName}::${params.level}`,
  });
}

export async function makeNotes(params: {
  studentId: string; conceptName: string; gradeLevel: number; board: string;
}): Promise<AskChinniTemplateResponse> {
  return _callTemplateWithCacheKey({
    studentId: params.studentId, conceptName: params.conceptName, gradeLevel: params.gradeLevel,
    templateId: 16, templateKey: 'make_notes',
    cacheKey: `${params.conceptName}::${params.board}`, topic2: params.board,
  });
}

export async function makeNotesVariant(params: {
  studentId: string; conceptName: string; gradeLevel: number; board: string;
  variant: 'simplify' | 'exam' | 'examples';
}): Promise<AskChinniTemplateResponse> {
  const templateMap = { simplify: 17, exam: 18, examples: 19 } as const;
  const keyMap = { simplify: 'make_notes_simplify', exam: 'make_notes_exam', examples: 'make_notes_examples' } as const;
  return _callTemplateWithCacheKey({
    studentId: params.studentId, conceptName: params.conceptName, gradeLevel: params.gradeLevel,
    templateId: templateMap[params.variant], templateKey: keyMap[params.variant],
    cacheKey: `${params.conceptName}::${params.board}::${params.variant}`, topic2: params.board,
  });
}

export interface MicroInteractionResult {
  interaction_json: unknown;
  from_cache: boolean;
  error?: string;
}

export async function getOrGenerateMicroInteraction(params: {
  conceptId: number;
  conceptName: string;
  subject: string;
  mode: 'real_life' | 'tips' | 'formula';
  contentText: string;
  gradeBand: string;
  conceptTable?: 'concepts' | 'concepts_plus1';
}): Promise<MicroInteractionResult> {
  const response = await fetch(`${supabaseUrl}/functions/v1/generate-micro-interaction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify({
      concept_id:    params.conceptId,
      concept_table: params.conceptTable ?? 'concepts_plus1',
      concept_name:  params.conceptName,
      subject:       params.subject,
      mode:          params.mode,
      content_text:  params.contentText,
      grade_band:    params.gradeBand,
    }),
  });
  if (!response.ok) return { interaction_json: null, from_cache: false, error: 'Generation failed' };
  return response.json();
}

export async function getOrGenerateArtifact(params: {
  conceptId: number; conceptName: string; subjectName: string; gradeLevel: number;
}): Promise<{ artifact_json: ArtifactBlob; from_cache: boolean; error?: string }> {
  const response = await fetch(`${supabaseUrl}/functions/v1/generate-artifact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify({
      concept_id: params.conceptId, concept_name: params.conceptName,
      subject: params.subjectName, grade_band: getGradeBand(params.gradeLevel),
      concept_table: 'concepts_plus1',
    }),
  });
  if (!response.ok) return { artifact_json: null as unknown as ArtifactBlob, from_cache: false, error: 'Generation failed' };
  return response.json();
}

export async function logArtifactInteraction(params: {
  studentId: string; conceptId: number; predictCorrect: boolean; reflectCorrect: boolean;
}): Promise<void> {
  await supabase.rpc('log_artifact_interaction', {
    p_student_id:      params.studentId,
    p_concept_id:      params.conceptId,
    p_predict_correct: params.predictCorrect,
    p_reflect_correct: params.reflectCorrect,
  });
}

export async function flagArtifact(conceptId: number, gradeBand: string): Promise<void> {
  await supabase.from('artifact_cache').update({ flagged: true })
    .eq('concept_id', conceptId).eq('concept_table', 'concepts_plus1').eq('grade_band', gradeBand);
}

export async function flagMicroInteraction(
  conceptId: number,
  gradeBand: string,
  mode: 'real_life' | 'tips' | 'formula',
  conceptTable: 'concepts' | 'concepts_plus1' = 'concepts_plus1',
): Promise<void> {
  await supabase.from('concept_micro_interactions')
    .update({ flagged: true, updated_at: new Date().toISOString() })
    .eq('concept_id', conceptId)
    .eq('grade_band', gradeBand)
    .eq('mode', mode)
    .eq('concept_table', conceptTable);
}

export async function flagChinniResponse(
  templateKey: string,
  gradeLevel: number,
  responseText: string,
): Promise<void> {
  await supabase.from('chinni_template_cache')
    .update({ flagged: true })
    .eq('template_key', templateKey)
    .eq('grade_level', gradeLevel)
    .eq('response_text', responseText);
}

export async function getConceptMap(conceptId: number): Promise<ConceptMapData> {
  const { data } = await supabase.rpc('get_concept_map', { p_concept_id: conceptId });
  return data as ConceptMapData;
}

export async function generateConceptMap(params: {
  conceptId: number; conceptName: string; subjectName: string; gradeLevel: number;
}): Promise<ConceptMapData> {
  const response = await fetch(`${supabaseUrl}/functions/v1/generate-concept-map`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify({
      concept_id: params.conceptId, concept_name: params.conceptName,
      subject: params.subjectName, grade_level: params.gradeLevel,
    }),
  });
  if (!response.ok) return { generated: false, needs_first: [], leads_to: [], related: [] };
  return response.json();
}

// ============================================================
// Applied Practice Questions
// ============================================================

export interface AppliedQuestion {
  question: string;
  context: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option_index: number;
  solution_steps: string[];
  final_answer: string;
  skill_id: string;
}

export type AppliedDifficulty = 'Basic' | 'Standard' | 'Advanced';

export async function getAppliedQuestions(
  conceptId: number,
  difficulty: AppliedDifficulty
): Promise<AppliedQuestion[] | null> {
  const { data } = await supabase
    .from('concept_applied_questions')
    .select('questions')
    .eq('concept_id', conceptId)
    .eq('concept_table', 'concepts_plus1')
    .eq('difficulty', difficulty)
    .eq('status', 'ready')
    .maybeSingle();
  const questions = (data?.questions as AppliedQuestion[]) ?? null;
  // Reject old-format rows that have no MCQ options — edge function will regenerate them
  if (questions && questions.length > 0 && !questions[0].option_a?.trim()) return null;
  return questions;
}

export async function generateAppliedQuestions(params: {
  conceptId: number;
  conceptName: string;
  subject: string;
  difficulty: AppliedDifficulty;
  gradeLevel: number;
  conceptType?: string;
}): Promise<{ status: 'ready' | 'generating' | 'error'; questions?: AppliedQuestion[] }> {
  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/generate-applied-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
      body: JSON.stringify({
        concept_id: params.conceptId,
        concept_name: params.conceptName,
        subject: params.subject,
        difficulty: params.difficulty,
        grade_level: params.gradeLevel,
        concept_type: params.conceptType,
        concept_table: 'concepts_plus1',
      }),
    });
    if (!response.ok) return { status: 'error' };
    return response.json();
  } catch {
    return { status: 'error' };
  }
}

export function pollAppliedQuestions(
  conceptId: number,
  difficulty: AppliedDifficulty,
  onReady: (questions: AppliedQuestion[]) => void,
  onTimeout: () => void,
  intervalMs = 4000,
  timeoutMs = 60000
): () => void {
  const start = Date.now();
  const id = setInterval(async () => {
    if (Date.now() - start > timeoutMs) {
      clearInterval(id);
      onTimeout();
      return;
    }
    const questions = await getAppliedQuestions(conceptId, difficulty);
    if (questions) {
      clearInterval(id);
      onReady(questions);
    }
  }, intervalMs);
  return () => clearInterval(id);
}

export interface AppliedAttempt {
  question_index: number;
  option_chosen: number;
  correct: boolean;
  attempt_number?: number;
  time_taken_seconds?: number | null;
}

export async function getAppliedAttempts(
  studentId: string,
  conceptId: number,
  difficulty: AppliedDifficulty,
): Promise<AppliedAttempt[]> {
  try {
    const { data, error } = await supabase.rpc('get_applied_attempts', {
      p_student_id: studentId,
      p_concept_id: conceptId,
      p_difficulty: difficulty,
    });
    if (error || !data) return [];
    return data as AppliedAttempt[];
  } catch {
    return [];
  }
}

export async function saveAppliedAttempt(params: {
  studentId: string;
  conceptId: number;
  difficulty: AppliedDifficulty;
  questionIndex: number;
  optionChosen: number;
  correct: boolean;
  timeTakenSeconds?: number;
}): Promise<{ isNew: boolean; attemptNumber: number }> {
  try {
    const { data, error } = await supabase.rpc('save_applied_attempt', {
      p_student_id: params.studentId,
      p_concept_id: params.conceptId,
      p_difficulty: params.difficulty,
      p_question_index: params.questionIndex,
      p_option_chosen: params.optionChosen,
      p_correct: params.correct,
      p_time_taken_seconds: params.timeTakenSeconds ?? null,
    });
    if (error) return { isNew: false, attemptNumber: 1 };
    const d = data as { is_new: boolean; attempt_number: number };
    return { isNew: d?.is_new ?? false, attemptNumber: d?.attempt_number ?? 1 };
  } catch {
    return { isNew: false, attemptNumber: 1 };
  }
}

// ── Applied Practice history (migration 10073) ────────────────
// Grouped by (concept, difficulty), not by date — there's no "sitting"
// concept here (migration 157), attempts accumulate per question slot with
// the latest attempt per question shown, same convention getAppliedAttempts
// above already uses.

export interface AppliedPracticeHistoryItem {
  concept_id: number;
  concept_name: string;
  difficulty: AppliedDifficulty;
  attempted_count: number;
  correct_count: number;
  total_questions: number;
  last_attempt_at: string;
}

export interface AppliedPracticeDetailQuestion {
  question_index: number;
  question: string;
  context: string | null;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option_index: number;
  solution_steps: string[];
  final_answer: string;
  option_chosen: number | null;
  correct: boolean | null;
  attempted_at: string | null;
}

export interface AppliedPracticeDetail {
  success: boolean;
  error?: string;
  concept_name: string;
  difficulty: AppliedDifficulty;
  questions: AppliedPracticeDetailQuestion[];
}

export async function getAppliedPracticeHistory(studentId: string): Promise<AppliedPracticeHistoryItem[]> {
  const { data, error } = await supabase.rpc('get_applied_practice_history', { p_student_id: studentId });
  if (error) {
    console.error('Error loading applied practice history:', error);
    return [];
  }
  return (data as AppliedPracticeHistoryItem[]) ?? [];
}

export async function getAppliedPracticeDetail(
  studentId: string,
  conceptId: number,
  difficulty: AppliedDifficulty,
): Promise<AppliedPracticeDetail | null> {
  const { data, error } = await supabase.rpc('get_applied_practice_detail', {
    p_student_id: studentId,
    p_concept_id: conceptId,
    p_difficulty: difficulty,
  });
  if (error) {
    console.error('Error loading applied practice detail:', error);
    return null;
  }
  return data as AppliedPracticeDetail;
}

// ============================================
// Concept Completion (frontend-driven, silent DB writes)
// ============================================

export async function recordConceptCompletion(
  studentId: string,
  conceptId: number,
  difficulty: string
): Promise<void> {
  supabase
    .from('student_concept_completions')
    .insert({ student_id: studentId, concept_id: conceptId, difficulty })
    .then(() => {});
}

export async function getCompletionCounts(studentId: string): Promise<{ total: number; today: number }> {
  try {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const { data, error } = await supabase
      .from('student_concept_completions')
      .select('completed_at')
      .eq('student_id', studentId);
    if (error || !data) return { total: 0, today: 0 };
    return {
      total: data.length,
      today: data.filter((r: { completed_at: string }) => new Date(r.completed_at) >= todayStart).length,
    };
  } catch {
    return { total: 0, today: 0 };
  }
}

export async function getPriorCompletionDifficulties(
  studentId: string,
  conceptId: number
): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('student_concept_completions')
      .select('difficulty')
      .eq('student_id', studentId)
      .eq('concept_id', conceptId);
    if (error || !data) return [];
    return data.map((r: { difficulty: string }) => r.difficulty);
  } catch {
    return [];
  }
}

export interface StudentSelfProfile {
  full_name: string;
  roll_number: string | null;
  grade_level: number;
  section_name: string | null;
}

export async function getStudentSelfProfile(studentId: string): Promise<StudentSelfProfile | null> {
  try {
    const { data, error } = await supabase.rpc('get_student_self_profile', { p_student_id: studentId });
    if (error || !data || data.length === 0) return null;
    return data[0] as StudentSelfProfile;
  } catch {
    return null;
  }
}
